/**
 * PanelSelector - Solar panel brand/model selection dropdowns
 *
 * Displays brand and model dropdowns with panel specifications.
 */

import React from 'react';

interface SolarPanel {
  brand: string;
  model: string;
  power_watts: number;
  width_mm: string;
  height_mm: string;
  area_m2: string;
  efficiency?: string;
  cell_type?: string;
}

interface PanelSelectorProps {
  selectedBrand: string;
  availableBrands: string[];
  selectedPanel: SolarPanel | null;
  availablePanels: SolarPanel[];
  panelsLoading: boolean;
  onBrandChange: (brand: string) => void;
  onPanelChange: (model: string) => void;
  onCustomDimensions: () => void;
}

export function PanelSelector({
  selectedBrand,
  availableBrands,
  selectedPanel,
  availablePanels,
  panelsLoading,
  onBrandChange,
  onPanelChange,
  onCustomDimensions,
}: PanelSelectorProps) {
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-semibold text-gray-700">Solar Panel Selection</h4>

      {/* Brand Dropdown */}
      <div>
        <label className="text-xs text-gray-600 block mb-1">Brand:</label>
        <select
          value={selectedBrand}
          onChange={(e) => onBrandChange(e.target.value)}
          className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded"
        >
          {availableBrands.length === 0 && (
            <option value="">Loading brands...</option>
          )}
          {availableBrands.map((brand: string) => (
            <option key={brand} value={brand}>
              {brand}
            </option>
          ))}
        </select>
      </div>

      {panelsLoading ? (
        <div className="text-xs text-gray-500">Loading panels...</div>
      ) : (
        <>
          {/* Model Dropdown */}
          <div>
            <label className="text-xs text-gray-600 block mb-1">Model:</label>
            <select
              value={selectedPanel?.model || ''}
              onChange={(e) => onPanelChange(e.target.value)}
              className="w-full text-xs px-2 py-1.5 border border-gray-300 rounded"
              disabled={availablePanels.length === 0}
            >
              {availablePanels.length === 0 && (
                <option value="">No panels available</option>
              )}
              {availablePanels.map((panel) => (
                <option key={panel.model} value={panel.model}>
                  {panel.model} - {panel.power_watts}W
                </option>
              ))}
            </select>
          </div>

          {/* Panel Info Display */}
          {selectedPanel && (
            <div className="bg-blue-50 p-2 rounded text-xs space-y-1">
              <div className="font-semibold text-blue-900">
                {selectedPanel.brand} {selectedPanel.model}
              </div>
              <div className="text-blue-700">
                <div>Power: {selectedPanel.power_watts}W</div>
                <div>
                  Size: {(parseFloat(selectedPanel.width_mm) / 1000).toFixed(3)}m × {(parseFloat(selectedPanel.height_mm) / 1000).toFixed(3)}m
                </div>
                <div>Area: {selectedPanel.area_m2}m²</div>
                {selectedPanel.efficiency && (
                  <div>Efficiency: {(parseFloat(selectedPanel.efficiency) * 100).toFixed(1)}%</div>
                )}
                {selectedPanel.cell_type && (
                  <div>Type: {selectedPanel.cell_type}</div>
                )}
              </div>
            </div>
          )}

          {/* Manual Override Option */}
          <div className="pt-2 border-t border-gray-200">
            <button
              onClick={onCustomDimensions}
              className="text-xs text-blue-600 hover:text-blue-800"
            >
              {selectedPanel ? '✏️ Custom dimensions' : '← Back to panel selection'}
            </button>
          </div>
        </>
      )}
    </div>
  );
}

export default PanelSelector;
